import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';
import { safeGetItem, safeSetItem } from '@/utils/storage/storageUtils';

type ScopedState<T> = { key: string | null; value: T };

const readScopedValue = <T>(key: string | null, defaults: T, validate: (value: unknown) => T): T => {
	if (!key || typeof window === 'undefined') return defaults;
	const stored = safeGetItem(key);
	if (stored === null) return defaults;
	try {
		return validate(JSON.parse(stored));
	} catch {
		return defaults;
	}
};

/**
 * Local-only state persisted under a dynamic localStorage key (e.g. scoped per user or property).
 *
 * Unlike `useLocalStorage`, this hook is safe for keys that change at runtime: when the key changes
 * (auth resolving, property switching) it reloads the new scope's stored value instead of leaking
 * the previous scope's value into it. It also never writes until the caller sets a value, so
 * merely visiting a page doesn't stamp defaults into storage. Pass `key: null` while the scope is
 * unknown; reads return `defaults` and writes are dropped until a real key arrives.
 *
 * `validate` receives whatever `JSON.parse` produced — possibly from an older app version or
 * hand-edited — and must coerce it to a usable T.
 */
export const useScopedLocalStorage = <T>(key: string | null, defaults: T, validate: (value: unknown) => T) => {
	const [state, setState] = useState<ScopedState<T>>(() => ({
		key,
		value: readScopedValue(key, defaults, validate),
	}));

	// Scope changed: reload during render (React's "adjust state when props change" pattern —
	// the render restarts before commit, so the stale value is never shown).
	if (state.key !== key) {
		setState({ key, value: readScopedValue(key, defaults, validate) });
	}

	const value = state.key === key ? state.value : defaults;
	const latestValueRef = useRef(value);
	latestValueRef.current = value;

	// Write-through: persisting at the moment of the set (instead of in an effect) means no
	// mount/reload write ever needs to be suppressed.
	const setValue: Dispatch<SetStateAction<T>> = useCallback(
		(next) => {
			const nextValue = typeof next === 'function' ? (next as (current: T) => T)(latestValueRef.current) : next;
			latestValueRef.current = nextValue;
			if (key) safeSetItem(key, JSON.stringify(nextValue));
			setState({ key, value: nextValue });
		},
		[key],
	);

	return [value, setValue] as const;
};
